// Reusable button style builder
// Usage:
//   import { buildButtonStyle } from "@/utils/buttonStyle";
//   const style = buildButtonStyle(appearance, { padding: "0px !important" });
// Notes:
// - Does not mutate the input appearance object.
// - Allows per-usage overrides via the second parameter.

export const BUTTON_TYPES = {
    FILL: "fill",
    OUTLINE: "outline",
    SOFT_SHADOW: "soft-shadow",
    HARD_SHADOW: "hard-shadow",
};

/**
 * Derive button_type from existing properties when button_type is missing
 */
export function deriveButtonType(props = {}) {
    const buttonColor = props.button_color ?? null;
    const shadowColor = props.button_shadow_color ?? null;
    const borderColor = props.button_border_color ?? null;

    // Outline: transparent bg with border
    if (buttonColor === "transparent" && borderColor) {
        return BUTTON_TYPES.OUTLINE;
    }

    // Check shadow types
    if (
        shadowColor &&
        shadowColor.trim() !== "" &&
        shadowColor.toLowerCase() !== "none"
    ) {
        // Hard shadow: "Xpx Ypx 0px" pattern (zero blur)
        if (/^\d+px\s+\d+px\s+0px/.test(shadowColor)) {
            return BUTTON_TYPES.HARD_SHADOW;
        }
        // Any other box-shadow implies a soft shadow variant in the UI.
        return BUTTON_TYPES.SOFT_SHADOW;
    }

    return BUTTON_TYPES.FILL;
}

export function buildButtonStyle(appearance = {}, overrides = {}) {
    const safe = (v, fallback = "") =>
        v === undefined || v === null ? fallback : v;
    const buttonType = appearance.button_type || deriveButtonType(appearance);
    const backgroundColor =
        buttonType === BUTTON_TYPES.OUTLINE
            ? "transparent"
            : safe(appearance.button_color);

    const base = {
        width: "100%",
        color: safe(appearance.button_font_color),
        backgroundColor,
        fontFamily: safe(appearance.fonts),
        fontWeight: safe(appearance.font_weight),
        padding: safe(overrides.padding, "10px !important"),
        height: "auto",
        margin: safe(appearance.button_margin, "6px 0px"),
        boxShadow: safe(appearance.button_shadow_color) || "none",
        transition: safe(appearance.transition, ""),
        // Border logic: support both simple and advanced (hard-shadow / border_color) cases
        border: computeBorder(appearance),
    };

    // Allow callers to override any computed property
    return { ...base, ...overrides };
}

function computeBorder(appearance = {}) {
    const borderColor = appearance.button_border_color;
    const type = appearance.button_type;

    if (type === BUTTON_TYPES.HARD_SHADOW) {
        // Keep compatibility with existing logic in UserProfile/Index.vue
        const color = borderColor || "#000";
        return `2px solid ${color}`;
    }

    if (borderColor) {
        return `1px solid ${borderColor} !important`;
    }

    // default (and compatible with MobileSimulator code)
    return "none !important";
}

export default buildButtonStyle;
